/**
 * useCapacitor Hook - React Hook for Mobile App Features
 * 
 * Provides easy access to mobile-specific functionality through
 * the CapacitorService for React components.
 */

import { useState, useEffect, useCallback } from 'react';
import { capacitorService } from '../services/CapacitorService';

export const useCapacitor = () => {
  const [isNative, setIsNative] = useState(false);
  const [platform, setPlatform] = useState('web');
  const [initialized, setInitialized] = useState(false);
  const [audioState, setAudioState] = useState({
    isPlaying: false,
    currentTrack: null,
    duration: 0,
    position: 0
  });

  // Initialize Capacitor service on mount
  useEffect(() => {
    const initializeCapacitor = async () => {
      try {
        await capacitorService.initialize();
        
        setIsNative(capacitorService.isNative);
        setPlatform(capacitorService.platform);
        setInitialized(true);
        
        console.log('Capacitor hook initialized successfully');
      } catch (error) {
        console.error('Failed to initialize Capacitor hook:', error);
      }
    };

    initializeCapacitor();
  }, []);

  // Background Audio Controls
  const playAudio = useCallback(async (audioUrl, metadata = {}) => {
    try {
      const success = await capacitorService.playAudio(audioUrl, metadata);
      
      if (success) {
        setAudioState(prev => ({
          ...prev,
          isPlaying: true,
          currentTrack: { url: audioUrl, ...metadata }
        }));
      }
      
      return success;
    } catch (error) {
      console.error('Failed to play audio:', error);
      return false;
    }
  }, []);

  const pauseAudio = useCallback(async () => {
    try {
      await capacitorService.pauseAudio();
      setAudioState(prev => ({ ...prev, isPlaying: false }));
    } catch (error) {
      console.error('Failed to pause audio:', error);
    }
  }, []);

  const resumeAudio = useCallback(async () => {
    try {
      await capacitorService.resumeAudio();
      setAudioState(prev => ({ ...prev, isPlaying: true }));
    } catch (error) {
      console.error('Failed to resume audio:', error);
    }
  }, []);

  const stopAudio = useCallback(async () => {
    try {
      await capacitorService.stopAudio();
      setAudioState({
        isPlaying: false,
        currentTrack: null,
        duration: 0,
        position: 0
      });
    } catch (error) {
      console.error('Failed to stop audio:', error);
    }
  }, []);

  // Offline Content Management
  const cacheAudio = useCallback(async (audioUrl, fileName) => {
    try {
      const cachedPath = await capacitorService.cacheAudioFile(audioUrl, fileName);
      return cachedPath;
    } catch (error) {
      console.error('Failed to cache audio:', error);
      return audioUrl;
    }
  }, []);

  const getCachedAudio = useCallback(async (fileName) => {
    try {
      const cachedPath = await capacitorService.getCachedAudioPath(fileName);
      return cachedPath;
    } catch (error) {
      console.error('Failed to get cached audio:', error);
      return null;
    }
  }, []);

  const clearCache = useCallback(async () => {
    try {
      await capacitorService.clearAudioCache();
      console.log('Audio cache cleared successfully');
    } catch (error) {
      console.error('Failed to clear cache:', error);
    }
  }, []);

  // Push Notifications
  const scheduleReminder = useCallback(async (title, body, delay) => {
    try {
      await capacitorService.scheduleReminderNotification(title, body, delay);
      console.log('Learning reminder scheduled');
    } catch (error) {
      console.error('Failed to schedule reminder:', error);
    }
  }, []);

  // Learning Analytics
  const trackProgress = useCallback(async (progressData) => {
    try {
      await capacitorService.sendLearningProgress(progressData);
    } catch (error) {
      console.error('Failed to track progress:', error);
    }
  }, []);

  // Platform Detection Helpers
  const isIOS = useCallback(() => capacitorService.isIOS(), []);
  const isAndroid = useCallback(() => capacitorService.isAndroid(), []);
  const isWeb = useCallback(() => capacitorService.isWeb(), []);

  // Mobile-specific UI helpers
  const hapticFeedback = useCallback(async (type = 'medium') => {
    try {
      if (isNative) {
        // Try Capacitor Haptics plugin if available (dynamic import to avoid hard dep)
        try {
          const spec = '@capacitor/haptics';
          // Use Vite ignore to avoid pre-bundling when not installed
          const mod = await import(/* @vite-ignore */ spec);
          const { Haptics, ImpactStyle, NotificationType } = mod;
          if (type === 'success' || type === 'warning' || type === 'error') {
            const map = { success: NotificationType.Success, warning: NotificationType.Warning, error: NotificationType.Error };
            await Haptics.notification({ type: map[type] });
          } else {
            const style = type === 'light' ? ImpactStyle.Light : type === 'heavy' ? ImpactStyle.Heavy : ImpactStyle.Medium;
            await Haptics.impact({ style });
          }
          return;
        } catch {
          // Fallback to web vibration API
        }
      }
      if (typeof navigator !== 'undefined' && 'vibrate' in navigator) {
        if (type === 'error' || type === 'warning') {
          navigator.vibrate([20, 40, 20]);
        } else if (type === 'success') {
          navigator.vibrate(30);
        } else {
          navigator.vibrate(20);
        }
      }
    } catch (error) {
      console.error('Haptic feedback not available:', error);
    }
  }, [isNative]);

  const showToast = useCallback(async (message) => {
    if (isNative) {
      try {
        // Toast would require @capacitor/toast plugin
        console.log('Native toast:', message);
      } catch (error) {
        console.error('Native toast not available:', error);
      }
    } else {
      // Fallback to web toast
      console.log('Web toast:', message);
    }
  }, [isNative]);

  return {
    // Status
    isNative,
    platform,
    initialized,
    
    // Audio Control
    audioState,
    playAudio,
    pauseAudio,
    resumeAudio,
    stopAudio,
    
    // Offline Content
    cacheAudio,
    getCachedAudio,
    clearCache,
    
    // Notifications
    scheduleReminder,
    
    // Analytics
    trackProgress,
    
    // Platform Detection
    isIOS,
    isAndroid,
    isWeb,
    
    // UI Helpers
    hapticFeedback,
    showToast,
    
    // Direct service access for advanced usage
    service: capacitorService
  };
};

export default useCapacitor;
